<script setup lang="ts">
  import AlertDialog from '@/components/ui/alert-dialog/AlertDialog.vue'
  import AlertDialogAction from '@/components/ui/alert-dialog/AlertDialogAction.vue'
  import AlertDialogCancel from '@/components/ui/alert-dialog/AlertDialogCancel.vue'
  import AlertDialogContent from '@/components/ui/alert-dialog/AlertDialogContent.vue'
  import AlertDialogDescription from '@/components/ui/alert-dialog/AlertDialogDescription.vue'
  import AlertDialogFooter from '@/components/ui/alert-dialog/AlertDialogFooter.vue'
  import AlertDialogHeader from '@/components/ui/alert-dialog/AlertDialogHeader.vue'
  import AlertDialogTitle from '@/components/ui/alert-dialog/AlertDialogTitle.vue'
  import Tooltip from '@/components/ui/tooltip/Tooltip.vue'
  import TooltipContent from '@/components/ui/tooltip/TooltipContent.vue'
  import TooltipProvider from '@/components/ui/tooltip/TooltipProvider.vue'
  import TooltipTrigger from '@/components/ui/tooltip/TooltipTrigger.vue'
  import { type DocumentStatus } from '@/composables/useDocumentProcessing'
  import { useDocumentThumbnail } from '@/composables/useDocumentThumbnail'
  import { useFeatureFlag } from '@/composables/useFeatureFlag'
  import { useOfficeDocument } from '@/composables/useOfficeDocument'
  import { useTranslation } from '@/composables/useTranslation'
  import { genericErrorToast } from '@/lib/toasts'
  import type { Document } from '@/types/generated'
  import { DocumentsService, FlagEnum } from '@/types/generated'
  import {
    AlertCircleIcon,
    CircleCheck,
    DownloadIcon,
    ExternalLinkIcon,
    FileIcon,
    FileTextIcon,
    LoaderCircle,
    PencilIcon,
    RotateCwIcon
  } from 'lucide-vue-next'
  import { computed, onMounted, ref } from 'vue'
  const { t } = useTranslation({
    en: {
      Document: 'Document',
      Documents: 'Documents',
      NoDocumentsYet: 'No documents yet',
      DeleteDocument: 'Delete document',
      DownloadDocument: 'Download document',
      confirm_delete: 'Delete Document',
      confirm_delete_document_message: 'Are you sure you want to delete this document?',
      delete: 'Delete',
      cancel: 'Cancel',
      ai_analysis_in_progress: 'AI analysis in progress',
      ai_can_produce_incorrect_results: 'The AI can produce incorrect results',
      ai_analysis_failed: 'AI analysis failed',
      please_upload_another_file: 'Please upload another file',
      please_retry_or_upload_another_file: 'Please retry or upload another file',
      retry_processing: 'Retry processing',
      try_to_process_document_again: 'Try to process the document again',
      ready_for_ai: 'Ready for AI',
      can_ask_questions_about_document: 'You can ask questions about this document',
      UploadingDocument: 'Uploading Document',
      Failed: 'Failed'
    },
    de: {
      Document: 'Dokument',
      Documents: 'Dokumente',
      NoDocumentsYet: 'Keine Dokumente vorhanden',
      DeleteDocument: 'Dokument löschen',
      DownloadDocument: 'Dokument herunterladen',
      confirm_delete: 'Dokument löschen',
      confirm_delete_document_message: 'Sind Sie sicher, dass Sie dieses Dokument löschen möchten?',
      delete: 'Löschen',
      cancel: 'Abbrechen',
      ai_analysis_in_progress: 'KI-Analyse läuft',
      ai_can_produce_incorrect_results: 'Die KI kann fehlerhafte Ergebnisse liefern',
      ai_analysis_failed: 'KI-Analyse fehlgeschlagen',
      please_upload_another_file: 'Bitte laden Sie eine andere Datei hoch',
      please_retry_or_upload_another_file:
        'Bitte wiederholen Sie den Vorgang oder laden Sie eine andere Datei hoch',
      retry_processing: 'Verarbeitung wiederholen',
      try_to_process_document_again: 'Versuchen Sie, das Dokument erneut zu verarbeiten',
      ready_for_ai: 'Bereit für KI',
      can_ask_questions_about_document: 'Sie können Fragen zu diesem Dokument stellen',
      UploadingDocument: 'Dokument wird hochgeladen',
      Failed: 'Fehlgeschlagen'
    }
  })

  const props = withDefaults(
    defineProps<{
      // Either provide documents directly (for static display) or use document processing module
      documents?: Document[] | DocumentStatus[]
      // Document processor to use for operations
      documentProcessor?: any
      showDelete?: boolean
      showDownload?: boolean
      showEdit?: boolean
      showRetry?: boolean
      compact?: boolean
      onDocumentDeleted?: (documentId: string) => Promise<void>
      onDocumentRetry?: (documentId: string) => Promise<void>
    }>(),
    {
      showDelete: true,
      showDownload: true,
      showEdit: false,
      showRetry: true,
      compact: false
    }
  )

  const emit = defineEmits<{
    documentDeleted: [documentId: string]
    documentRetry: [documentId: string]
  }>()

  const officeEditingFlag = useFeatureFlag(FlagEnum.OFFICE_DOCUMENT_EDITING)
  const { isOfficeFile, openOfficeEditor, getOfficeFileAriaLabel } = useOfficeDocument()
  const { getThumbnailUrl } = useDocumentThumbnail()

  const showDeleteConfirmation = ref(false)
  const documentToDelete = ref<Document | null>(null)

  // Get documents from either props or document processing module
  const documents = computed(() => {
    let docs: (Document | DocumentStatus)[] = []

    if (props.documents) {
      // Use provided documents (static display)
      docs = props.documents
    } else if (props.documentProcessor?.documents) {
      // Use document processing module for real-time state tracking
      // Access the ref value directly since it's already a ref
      docs = props.documentProcessor.documents.value || props.documentProcessor.documents
    }

    if (docs.length === 0) return []

    // Sort documents: failed > in-progress > complete, then by date (newest first)
    return docs.sort((a: Document | DocumentStatus, b: Document | DocumentStatus) => {
      // Helper function to get document status
      const getStatus = (doc: Document | DocumentStatus) => {
        if ('isUploading' in doc) {
          // DocumentStatus object
          if (doc.hasError) return 'error'
          if (doc.isUploading || doc.isProcessing) return 'in-progress'
          return 'complete'
        }
        // Raw Document object - determine status from document properties
        if (doc.metadata_enrichment_error) return 'error'
        if (!doc.metadata_enrichment_is_finished && !doc.metadata_enrichment_error)
          return 'in-progress'
        return 'complete'
      }

      const aStatus = getStatus(a)
      const bStatus = getStatus(b)

      // Priority: error > in-progress > complete
      if (aStatus === 'error' && bStatus !== 'error') return -1
      if (aStatus !== 'error' && bStatus === 'error') return 1

      if (aStatus === 'in-progress' && bStatus === 'complete') return -1
      if (aStatus === 'complete' && bStatus === 'in-progress') return 1

      // Within same priority, sort by date (newest first)
      const aTime =
        'uploadInfo' in a
          ? a.uploadInfo?.started || 0
          : 'created_at' in a
            ? new Date(a.created_at).getTime()
            : 0
      const bTime =
        'uploadInfo' in b
          ? b.uploadInfo?.started || 0
          : 'created_at' in b
            ? new Date(b.created_at).getTime()
            : 0

      return bTime - aTime
    })
  })

  onMounted(async () => {
    await officeEditingFlag.checkFlag()
  })

  // === HELPER FUNCTIONS ===

  const truncateFilename = (filename: string, maxLength: number = 26) => {
    if (!filename || filename.length <= maxLength) return filename

    const extension = filename.split('.').pop()
    const name = filename.substring(0, filename.lastIndexOf('.'))
    const truncatedLength = maxLength - extension!.length - 3
    const truncatedName = name.substring(0, truncatedLength) + '...'

    return `${truncatedName}.${extension}`
  }

  const formatDate = (date: string | null) => {
    if (!date) return ''
    return new Date(date).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const downloadDocument = (doc: Document | DocumentStatus) => {
    // Get the actual document object
    const actualDoc = getActualDocument(doc)

    if (!actualDoc) {
      console.error('No document available for download')
      return
    }

    if (actualDoc.url) {
      const link = document.createElement('a')
      link.href = actualDoc.url
      link.download = actualDoc.filename || 'document'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      return
    }

    if ((actualDoc as any).access_token && actualDoc.uuid) {
      const url = `${window.location.origin}/documents/${actualDoc.uuid}/download/${(actualDoc as any).access_token}/`
      const link = document.createElement('a')
      link.href = url
      link.download = actualDoc.filename || 'document'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      return
    }

    console.error('Unable to download document - no URL or access token available', actualDoc)
  }

  const openDocumentInNewTab = (doc: Document | DocumentStatus) => {
    // Get the actual document object
    const actualDoc = getActualDocument(doc)

    if (!actualDoc) {
      console.error('No document available')
      return
    }

    if (officeEditingFlag.isEnabled.value && isOfficeFile(actualDoc)) {
      openOfficeEditor(actualDoc as any)
      return
    }

    if (!actualDoc.url) return
    window.open(actualDoc.url, '_blank')
  }

  // === DELETE FUNCTIONALITY ===

  const confirmDeleteDocument = (document: Document | DocumentStatus) => {
    // Get the actual document object
    const actualDoc = getActualDocument(document)

    if (!actualDoc) {
      console.error('No document available for deletion')
      return
    }

    documentToDelete.value = actualDoc
    showDeleteConfirmation.value = true
  }

  const deleteDocument = async () => {
    if (!documentToDelete.value?.uuid) {
      showDeleteConfirmation.value = false
      documentToDelete.value = null
      return
    }

    try {
      await DocumentsService.documentsDestroy({ uuid: documentToDelete.value.uuid })

      // Emit event for parent to handle refresh
      emit('documentDeleted', documentToDelete.value.uuid)

      // Call parent callback if provided
      if (props.onDocumentDeleted) {
        await props.onDocumentDeleted(documentToDelete.value.uuid)
      }
    } catch (error) {
      console.error('Error deleting document:', error)
      genericErrorToast()
    } finally {
      showDeleteConfirmation.value = false
      documentToDelete.value = null
    }
  }

  // === RETRY FUNCTIONALITY ===

  const retryDocument = async (document: Document | DocumentStatus) => {
    const uuid = getDocumentUuid(document)
    if (!uuid) return

    try {
      // Emit event for parent to handle retry
      emit('documentRetry', uuid)

      // Call parent callback if provided
      if (props.onDocumentRetry) {
        await props.onDocumentRetry(uuid)
      }
    } catch (error) {
      console.error('Error retrying document:', error)
      genericErrorToast()
    }
  }

  // === DOCUMENT STATUS HELPERS ===

  const getDocumentStatus = (doc: Document | DocumentStatus) => {
    // If it's a DocumentStatus object, use its status flags
    if ('isUploading' in doc) {
      if (doc.isUploading) return 'uploading'
      if (doc.isProcessing) return 'processing'
      if (doc.hasError) return 'error'
      if (doc.isComplete) return 'complete'
      return 'uploading'
    }

    // If it's a raw Document object, check all error fields
    const hasError = Boolean(
      doc.metadata_enrichment_error ||
        doc.ocr_error ||
        doc.embeddings_error ||
        doc.summary_error ||
        doc.analysis_error ||
        doc.raptor_summaries_error
    )

    if (hasError) {
      return 'error'
    }

    // Check if still processing
    const isProcessing = Boolean(
      doc.metadata_enrichment_started_at && !doc.metadata_enrichment_is_finished
    )

    if (isProcessing) {
      return 'processing'
    }

    // Default to complete
    return 'complete'
  }

  const getDocumentUuid = (doc: Document | DocumentStatus) => {
    if ('uuid' in doc) {
      return doc.uuid
    }
    return ''
  }

  const getDocumentThumbnailUrl = (doc: Document | DocumentStatus) => {
    // If it's a DocumentStatus object, get thumbnail from the document property
    if ('document' in doc && doc.document) {
      return getThumbnailUrl(doc.document)
    }

    // If it's a raw Document object, get thumbnail directly
    if ('images' in doc) {
      return getThumbnailUrl(doc as Document)
    }

    return null
  }

  const getActualDocument = (doc: Document | DocumentStatus): Document | null => {
    // If it's a DocumentStatus object, get the document property
    if ('document' in doc) {
      return doc.document || null
    }

    // If it's a raw Document object, return it directly
    if ('images' in doc) {
      return doc as Document
    }

    return null
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'complete':
        return CircleCheck
      case 'processing':
        return LoaderCircle
      case 'error':
        return AlertCircleIcon
      default:
        return LoaderCircle
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'complete':
        return 'text-green-500'
      case 'processing':
        return 'text-violet-500'
      case 'error':
        return 'text-red-500'
      default:
        return 'text-violet-500'
    }
  }

  const getStatusTooltip = (status: string) => {
    switch (status) {
      case 'complete':
        return {
          title: t('ready_for_ai'),
          description: t('can_ask_questions_about_document')
        }
      case 'processing':
        return {
          title: t('ai_analysis_in_progress'),
          description: t('ai_can_produce_incorrect_results')
        }
      case 'error':
        return {
          title: t('ai_analysis_failed'),
          description: t('please_retry_or_upload_another_file')
        }
      default:
        return {
          title: t('UploadingDocument'),
          description: ''
        }
    }
  }
</script>

<template>
  <div
    class="w-full"
    data-cy="documents-list"
  >
    <!-- Documents List -->
    <div
      v-if="documents.length > 0"
      class="space-y-2"
      data-cy="documents-list-items"
    >
      <div
        v-for="doc in documents"
        :key="getDocumentUuid(doc)"
        :class="['flex items-center px-1 py-2', props.compact ? 'gap-2' : 'gap-4']"
        :data-cy="`document-item-${getDocumentUuid(doc)}`"
      >
        <!-- Document thumbnail -->
        <div class="shrink-0">
          <div class="h-10 w-10 overflow-hidden rounded border border-gray-100 bg-white">
            <img
              v-if="getDocumentThumbnailUrl(doc)"
              :src="getDocumentThumbnailUrl(doc) || ''"
              alt="Document thumbnail"
              class="h-full w-full object-cover"
            />
            <FileIcon
              v-else
              class="h-full w-full p-2 text-gray-400"
            />
          </div>
        </div>

        <!-- Document Info -->
        <div class="min-w-0 flex-1 overflow-hidden">
          <!-- Document name with accessibility enhancements -->
          <button
            v-if="'document' in doc && doc.document?.url"
            class="group block w-full min-w-0 cursor-pointer rounded text-left text-sm font-medium
              hover:text-violet-600 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
            :aria-label="
              getOfficeFileAriaLabel(
                doc.filename,
                officeEditingFlag.isEnabled && isOfficeFile(doc)
              ) +
              ' - Click to ' +
              (officeEditingFlag.isEnabled && isOfficeFile(doc) ? 'edit' : 'view')
            "
            @click="openDocumentInNewTab('document' in doc ? doc.document : doc)"
            @keydown.enter="openDocumentInNewTab('document' in doc ? doc.document : doc)"
            @keydown.space.prevent="openDocumentInNewTab('document' in doc ? doc.document : doc)"
          >
            <span class="inline-flex min-w-0 items-center gap-1 group-hover:underline">
              <span class="truncate">
                {{ truncateFilename(doc.filename) }}
              </span>
              <ExternalLinkIcon
                v-if="!officeEditingFlag.isEnabled || !isOfficeFile(doc)"
                class="ml-1 h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                aria-hidden="true"
              />
              <PencilIcon
                v-if="officeEditingFlag.isEnabled && isOfficeFile(doc)"
                class="ml-1 h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                aria-hidden="true"
              />
            </span>
          </button>
          <span
            v-else
            class="block truncate text-sm font-medium text-gray-500"
            :aria-label="getOfficeFileAriaLabel(doc.filename, false) + ' - Processing'"
          >
            {{ truncateFilename(doc.filename) }}
          </span>

          <!-- Progress bar for uploads -->
          <div
            v-if="'isUploading' in doc && doc.isUploading"
            class="mt-1"
          >
            <div class="flex items-center gap-2">
              <div class="relative h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  class="absolute left-0 top-0 h-full rounded-full bg-violet-500 transition-all duration-300"
                  :style="{ width: `${'progress' in doc ? doc.progress : 0}%` }"
                ></div>
              </div>
              <span class="whitespace-nowrap text-xs font-medium text-gray-700">
                {{ Math.round('progress' in doc ? doc.progress : 0) }}%
              </span>
            </div>
          </div>

          <!-- Document date (for all documents including processing ones) -->
          <div
            v-if="'document' in doc ? doc.document : doc"
            class="mt-1 text-xs text-gray-500"
          >
            {{
              (
                'document' in doc
                  ? doc.document?.created_at
                  : 'created_at' in doc
                    ? doc.created_at
                    : null
              )
                ? formatDate(
                    'document' in doc
                      ? doc.document?.created_at || ''
                      : 'created_at' in doc
                        ? doc.created_at || ''
                        : ''
                  )
                : ''
            }}
          </div>
        </div>

        <!-- Status and Actions -->
        <div class="flex shrink-0 items-center gap-2">
          <!-- Status Icon with Tooltips -->
          <template v-if="getDocumentStatus(doc) !== 'uploading'">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <component
                    :is="getStatusIcon(getDocumentStatus(doc))"
                    :class="[
                      'h-5 w-5',
                      getStatusColor(getDocumentStatus(doc)),
                      getDocumentStatus(doc) === 'processing' ? 'animate-spin' : ''
                    ]"
                  />
                </TooltipTrigger>
                <TooltipContent
                  class="!z-[9999] max-w-[200px] rounded-md border-0 bg-gray-800 px-3 py-2 text-white shadow-lg"
                  :side="'top'"
                >
                  <div class="flex flex-col">
                    <p class="text-sm font-medium">
                      {{ getStatusTooltip(getDocumentStatus(doc)).title }}
                    </p>
                    <p
                      v-if="getStatusTooltip(getDocumentStatus(doc)).description"
                      class="text-xs text-gray-300"
                    >
                      {{ getStatusTooltip(getDocumentStatus(doc)).description }}
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </template>

          <!-- Action Buttons -->
          <div class="flex gap-1">
            <!-- Download Button -->
            <TooltipProvider
              v-if="
                showDownload &&
                (getDocumentStatus(doc) === 'complete' || getDocumentStatus(doc) === 'processing')
              "
            >
              <Tooltip>
                <TooltipTrigger>
                  <button
                    class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white
                      shadow-sm hover:bg-gray-50"
                    data-cy="document-download-button"
                    @click="downloadDocument(doc)"
                  >
                    <DownloadIcon class="h-4 w-4 text-gray-800" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  class="!z-[9999] max-w-[200px] rounded-md border-0 bg-gray-800 px-3 py-2 text-white shadow-lg"
                  :side="'top'"
                >
                  <p class="text-sm">{{ t('DownloadDocument') }}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <!-- Edit Button (Office Documents) -->
            <TooltipProvider
              v-if="
                showEdit &&
                officeEditingFlag.isEnabled.value &&
                getActualDocument(doc) &&
                isOfficeFile(getActualDocument(doc)!)
              "
            >
              <Tooltip>
                <TooltipTrigger>
                  <button
                    class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white
                      shadow-sm hover:bg-gray-50"
                    :aria-label="
                      getActualDocument(doc)
                        ? getOfficeFileAriaLabel(getActualDocument(doc)!.filename)
                        : ''
                    "
                    @click="openDocumentInNewTab(doc)"
                  >
                    <PencilIcon class="h-4 w-4 text-gray-800" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  class="!z-[9999] max-w-[200px] rounded-md border-0 bg-gray-800 px-3 py-2 text-white shadow-lg"
                  :side="'top'"
                >
                  <p class="text-sm">Edit Document</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <!-- Retry Button -->
            <TooltipProvider v-if="showRetry && getDocumentStatus(doc) === 'error'">
              <Tooltip>
                <TooltipTrigger>
                  <button
                    class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white
                      shadow-sm hover:bg-gray-50"
                    @click="retryDocument(doc)"
                  >
                    <RotateCwIcon class="h-4 w-4 text-gray-800" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  class="!z-[9999] max-w-[200px] rounded-md border-0 bg-gray-800 px-3 py-2 text-white shadow-lg"
                  :side="'top'"
                >
                  <p class="text-sm">{{ t('retry_processing') }}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <!-- Delete Button -->
            <!-- <TooltipProvider v-if="showDelete">
              <Tooltip>
                <TooltipTrigger>
                  <button
                    class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white
                      shadow-sm hover:bg-gray-50"
                    @click="confirmDeleteDocument(doc)"
                  >
                    <LucideTrash2 class="h-4 w-4 text-gray-800" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  class="!z-[9999] max-w-[200px] rounded-md border-0 bg-gray-800 px-3 py-2 text-white shadow-lg"
                  :side="'top'"
                >
                  <p class="text-sm">{{ t('DeleteDocument') }}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider> -->
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else
      class="flex flex-col items-center justify-center py-8 text-center"
    >
      <FileTextIcon class="h-12 w-12 text-gray-400" />
      <p class="mt-2 text-sm text-gray-500">{{ t('NoDocumentsYet') }}</p>
    </div>
  </div>

  <!-- Delete Confirmation Modal -->
  <AlertDialog v-model:open="showDeleteConfirmation">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{{ t('confirm_delete') }}</AlertDialogTitle>
        <AlertDialogDescription>
          {{ t('confirm_delete_document_message') }}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel @click="showDeleteConfirmation = false">
          {{ t('cancel') }}
        </AlertDialogCancel>
        <AlertDialogAction @click="deleteDocument">
          {{ t('delete') }}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
